/**
 * Steal from Masters — extraction (docs/design/STEAL-SPEC.md "Extraction").
 *
 * Ten pure detectors over an analyzed piece's Chord[], one per move where the
 * Chord model actually carries the signal. Each returns a {numerator,
 * denominator} pair; counts accumulate across pieces (see learnPiece) so a
 * weight is always numerator/denominator over ALL pieces learned so far, not
 * an average of per-piece rates.
 *
 * v1 honesty limits (mirrors the moves engine's own candor in
 * src/lib/moves/moves/*.ts):
 * - No inversion tracking in the Chord model → "avoids root-position V"-class
 *   traits are impossible to detect; not attempted here.
 * - Mixture detection (darken-it, sidestep) inherits darken-it's convention:
 *   a true borrowed chord has no field for a literal flatted root (see
 *   src/lib/moves/pitch-utils.ts), so "borrowed" here means isChromatic with
 *   chromaticType 'borrowed' at the specific scaleDegree/quality combinations
 *   darken-it, brighten-the-ending, and sidestep itself actually produce.
 * - Weights are per-detector ratios over different denominators (all chords,
 *   vs. chord pairs, vs. cadence-adjacent chords, vs. pieces) — do NOT
 *   compare weights across moves as if commensurable. A 0.6 on widen-it and
 *   a 0.6 on brighten-the-ending do not mean "equally likely"; they rank a
 *   composer's habits within each move's own frame, not against each other.
 */
import { Note } from 'tonal';
import type { Chord, Mode } from '@/types/chord';
import type { Influence, InfluencePiece, MoveId, Trait } from '@/types/influence';

export interface DetectorResult {
  numerator: number;
  denominator: number;
}

/** Canonical order — matches the spec's extraction list. */
export const STEAL_MOVE_IDS: MoveId[] = [
  'widen-it',
  'suspend-into-it',
  'darken-it',
  'delay-the-arrival',
  'brighten-the-ending',
  'take-the-long-way-home',
  'sidestep',
  'let-the-bass-hold',
  'walk-the-bass',
  'thin-it',
];

function hasAnyExtension(extensions: Chord['extensions']): boolean {
  return Object.values(extensions).some(Boolean);
}

const PLAIN_TRIAD_QUALITIES = new Set(['major', 'minor', 'diminished', 'augmented']);

/**
 * The three borrowed-chord patterns the OTHER detectors already explain —
 * darken-it's two mixtures and brighten-the-ending's Picardy third. sidestep
 * counts only borrowed chords that DON'T match one of these, so an ending
 * Picardy third isn't double-counted as an "unexplained" sidestep.
 */
function isExplainedBorrowedPattern(chord: Chord): boolean {
  return (
    (chord.scaleDegree === 4 && chord.quality === 'minor') || // darken-it: IV -> iv
    (chord.scaleDegree === 6 && chord.quality === 'major') || // darken-it: vi -> VI
    (chord.scaleDegree === 1 && chord.quality === 'major') // brighten-the-ending: i -> I
  );
}

// ─────────────────────────── Detectors ───────────────────────────

function detectWidenIt(chords: Chord[]): DetectorResult {
  return {
    numerator: chords.filter((c) => !!c.extensions.add9).length,
    denominator: chords.length,
  };
}

/**
 * "Cadence-adjacent" = the chord immediately before a tonic (I/i) or
 * dominant (V) arrival — the two spots a 4-3 suspension idiomatically leans
 * into. Numerator is the SUBSET of those spots where the composer actually
 * suspended, not all sus chords overall — the trait reads "how often does he
 * suspend when a cadence is close," not "how many sus chords exist."
 */
function detectSuspendIntoIt(chords: Chord[]): DetectorResult {
  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < chords.length - 1; i++) {
    const next = chords[i + 1];
    if (next.scaleDegree === 1 || next.scaleDegree === 5) {
      denominator++;
      const chord = chords[i];
      if (chord.extensions.sus4 || chord.extensions.sus2) numerator++;
    }
  }
  return { numerator, denominator };
}

function detectDarkenIt(chords: Chord[]): DetectorResult {
  const numerator = chords.filter(
    (c) =>
      c.isChromatic &&
      c.chromaticType === 'borrowed' &&
      ((c.scaleDegree === 4 && c.quality === 'minor') || (c.scaleDegree === 6 && c.quality === 'major'))
  ).length;
  return { numerator, denominator: chords.length };
}

/** V -> vi motions / V -> anything motions (scale-degree bigram; quality not required for the ratio). */
function detectDelayTheArrival(chords: Chord[]): DetectorResult {
  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < chords.length - 1; i++) {
    if (chords[i].scaleDegree === 5) {
      denominator++;
      if (chords[i + 1].scaleDegree === 6) numerator++;
    }
  }
  return { numerator, denominator };
}

/**
 * Piece-level, not chord-level: a minor-mode piece either ends on a Picardy
 * third or it doesn't (0 or 1), and only minor-mode pieces count toward the
 * denominator at all — matching the spec's "minor pieces" framing. Weight
 * accumulates across pieces exactly like the chord-level detectors (see
 * learnPiece), so after N minor pieces where M end major, weight = M/N.
 */
function detectBrightenTheEnding(chords: Chord[], mode: Mode): DetectorResult {
  if (mode !== 'minor' || chords.length === 0) return { numerator: 0, denominator: 0 };
  const last = chords[chords.length - 1];
  const endsMajor = last.scaleDegree === 1 && last.quality === 'major';
  return { numerator: endsMajor ? 1 : 0, denominator: 1 };
}

function detectTakeTheLongWayHome(chords: Chord[]): DetectorResult {
  const numerator = chords.filter((c) => c.chromaticType === 'secondary').length;
  return { numerator, denominator: chords.length };
}

function detectSidestep(chords: Chord[]): DetectorResult {
  const numerator = chords.filter(
    (c) => c.isChromatic && c.chromaticType === 'borrowed' && !isExplainedBorrowedPattern(c)
  ).length;
  return { numerator, denominator: chords.length };
}

/** "Sharing a bass note" means the literal same pitch (note + octave), not just pitch class — a pedal holds one string, not one letter name. */
function detectLetTheBassHold(chords: Chord[]): DetectorResult {
  let numerator = 0;
  const denominator = Math.max(0, chords.length - 1);
  for (let i = 0; i < chords.length - 1; i++) {
    if (chords[i].voices.bass === chords[i + 1].voices.bass) numerator++;
  }
  return { numerator, denominator };
}

/**
 * Bass motions only — a held bass (interval 0) is a DIFFERENT trait
 * (let-the-bass-hold) and is excluded from both numerator and denominator
 * here so the two detectors don't compete over the same pairs. Stepwise
 * mirrors walk-the-bass's own LEAP_THRESHOLD_SEMITONES (3): anything smaller
 * counts as a step.
 */
function detectWalkTheBass(chords: Chord[]): DetectorResult {
  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < chords.length - 1; i++) {
    const a = Note.midi(chords[i].voices.bass);
    const b = Note.midi(chords[i + 1].voices.bass);
    if (a === null || b === null) continue;
    const interval = Math.abs(b - a);
    if (interval === 0) continue;
    denominator++;
    if (interval < 3) numerator++;
  }
  return { numerator, denominator };
}

function detectThinIt(chords: Chord[]): DetectorResult {
  const numerator = chords.filter((c) => !hasAnyExtension(c.extensions) && PLAIN_TRIAD_QUALITIES.has(c.quality)).length;
  return { numerator, denominator: chords.length };
}

const DETECTORS: Record<MoveId, (chords: Chord[], mode: Mode) => DetectorResult> = {
  'widen-it': detectWidenIt,
  'suspend-into-it': detectSuspendIntoIt,
  'darken-it': detectDarkenIt,
  'delay-the-arrival': detectDelayTheArrival,
  'brighten-the-ending': detectBrightenTheEnding,
  'take-the-long-way-home': detectTakeTheLongWayHome,
  sidestep: detectSidestep,
  'let-the-bass-hold': detectLetTheBassHold,
  'walk-the-bass': detectWalkTheBass,
  'thin-it': detectThinIt,
};

/** Run every detector over one piece. Chords are sorted by startBeat first — the detectors are order-sensitive (motions, adjacency). */
export function extractTraits(chords: Chord[], mode: Mode): Record<MoveId, DetectorResult> {
  const sorted = [...chords].sort((a, b) => a.startBeat - b.startBeat);
  const result = {} as Record<MoveId, DetectorResult>;
  for (const moveId of STEAL_MOVE_IDS) {
    result[moveId] = DETECTORS[moveId](sorted, mode);
  }
  return result;
}

const EVIDENCE_VERBS: Record<MoveId, (n: number, d: number) => string> = {
  'widen-it': (n, d) => `adds a ninth in ${n} of ${d} chords`,
  'suspend-into-it': (n, d) => `suspends into the cadence in ${n} of ${d} approaches`,
  'darken-it': (n, d) => `borrows a darker chord in ${n} of ${d} chords`,
  'delay-the-arrival': (n, d) => `delays the arrival in ${n} of ${d} approaches to V`,
  'brighten-the-ending': (n, d) => `ends a minor piece in major in ${n} of ${d} pieces`,
  'take-the-long-way-home': (n, d) => `takes the long way home in ${n} of ${d} chords`,
  sidestep: (n, d) => `sidesteps into an unexplained color in ${n} of ${d} chords`,
  'let-the-bass-hold': (n, d) => `lets the bass hold in ${n} of ${d} chord pairs`,
  'walk-the-bass': (n, d) => `walks the bass in ${n} of ${d} bass motions`,
  'thin-it': (n, d) => `keeps it a plain triad in ${n} of ${d} chords`,
};

/** Evidence strings regenerate from totals — never stored independently of numerator/denominator. */
export function formatEvidence(moveId: MoveId, numerator: number, denominator: number): string {
  if (denominator === 0) return 'not enough evidence yet';
  return EVIDENCE_VERBS[moveId](numerator, denominator);
}

function weightFor(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

/**
 * "Learn this piece": run extraction and accumulate onto an influence's
 * traits (numerators/denominators sum across every piece learned so far;
 * weight and evidence are always recomputed from the running totals, never
 * carried over stale). Pure — returns a new Influence, doesn't mutate.
 */
export function learnPiece(
  influence: Influence,
  chords: Chord[],
  mode: Mode,
  pieceName: string
): Influence {
  const detected = extractTraits(chords, mode);

  const traits: Trait[] = STEAL_MOVE_IDS.map((moveId) => {
    const existing = influence.traits.find((t) => t.moveId === moveId);
    const numerator = (existing?.numerator ?? 0) + detected[moveId].numerator;
    const denominator = (existing?.denominator ?? 0) + detected[moveId].denominator;
    return {
      moveId,
      numerator,
      denominator,
      weight: weightFor(numerator, denominator),
      evidence: formatEvidence(moveId, numerator, denominator),
    };
  });

  const piece: InfluencePiece = {
    name: pieceName,
    chordCount: chords.length,
    learnedAt: new Date().toISOString(),
  };

  return {
    ...influence,
    traits,
    pieces: [...influence.pieces, piece],
  };
}
